import { utcToZonedTime } from 'date-fns-tz';
import Highcharts, { Options as HighchartsOptions, SeriesColumnOptions, XAxisPlotBandsOptions } from 'highcharts';
import HighchartsBoost from 'highcharts/modules/boost';
import HighchartsReact from 'highcharts-react-official';
import { TFunction } from 'i18next';
import React, { memo } from 'react';

import { PLOT_BAND_COLOR, StackType, staticChartOptions } from './config';
import { multipleQueueTooltipFormatter, tooltipFormatter } from './utils';
import { useAuth } from '../../../../app/AuthorizationProvider';
import { RecordBooleanType } from '../../../../types';
import { getZonedDateTimezoneOffset } from '../../../../utils/date';
import { MIN_15, REQUIRED_STAFF_COLOR, SCHEDULED_STAFF_COLOR } from '../../config';
import { MissingForecastPerQueue, QueuesWithColors, SchedulingChartProps as ChartProps } from '../../types';

HighchartsBoost(Highcharts);

const PLOT_BAND_LEFT_ALIGN_OFFSET = 58000;
const PLOT_BAND_RIGHT_ALIGN_OFFSET = 8000;

/* eslint-disable react/no-unused-prop-types */
export interface SchedulingChartProps extends ChartProps {
  dataPerQueueVisibility: RecordBooleanType;
  interval?: number;
  isLoadingData: boolean;
  isMultiQueueSchedule: boolean;
  missingForecastIntervalsPerQueue: MissingForecastPerQueue;
  queueIds: string[];
  queues: QueuesWithColors;
  t: TFunction;
  timezone: string;
  width: number;
}
/* eslint-enable react/no-unused-prop-types */

const generateChartConfig: (props: SchedulingChartProps, locale: string, localeObj?: Locale) => HighchartsOptions = (
  {
    dataPerQueue,
    dataPerQueueVisibility,
    interval = MIN_15,
    isLoadingData,
    isMultiQueueSchedule,
    missingForecastIntervalsPerQueue,
    plotBandsData,
    queueIds,
    queues,
    t,
    timezone,
    yAxisTicks,
    width,
  },
  locale,
  localeObj
) => {
  const halfOfInterval = interval / 2;

  return {
    ...staticChartOptions,
    chart: {
      ...staticChartOptions.chart,
      width,
    },
    series: isLoadingData
      ? []
      : queueIds.reduce((series: SeriesColumnOptions[], queueId) => {
          const { color } = queues[queueId];
          const defaultSeriesOptions = (staticChartOptions.series as SeriesColumnOptions[])[0];

          series.push({
            ...defaultSeriesOptions,
            // adds 0.5 opacity
            color: isMultiQueueSchedule && color ? `${color}80` : REQUIRED_STAFF_COLOR,
            data: [...(dataPerQueue?.[queueId]?.requiredStaff ?? [])],
            id: `${queueId}__${StackType.REQUIRED}`,
            name: queues[queueId].name,
            stack: StackType.REQUIRED,
            type: 'column',
            visible: dataPerQueueVisibility[queueId],
          });
          series.push({
            ...defaultSeriesOptions,
            color: (isMultiQueueSchedule && color) || SCHEDULED_STAFF_COLOR,
            data: [...(dataPerQueue?.[queueId]?.scheduledStaff ?? [])],
            id: `${queueId}__${StackType.STAFFED}`,
            name: queues[queueId].name,
            stack: StackType.STAFFED,
            type: 'column',
            visible: dataPerQueueVisibility[queueId],
          });

          return series;
        }, []),
    time: {
      getTimezoneOffset: (timestamp: number) =>
        getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
      useUTC: true,
    },
    tooltip: {
      ...staticChartOptions.tooltip,
      formatter: isMultiQueueSchedule
        ? multipleQueueTooltipFormatter(
            t,
            interval,
            timezone,
            locale,
            localeObj,
            dataPerQueue,
            missingForecastIntervalsPerQueue
          )
        : tooltipFormatter(t, interval, timezone, locale, localeObj, dataPerQueue),
    },
    xAxis: {
      ...staticChartOptions.xAxis,
      plotBands:
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [] || // remove this line to unblock this functionality
        plotBandsData.reduce((plotBands, { start, end, queueIds: highlightedQueueIds }) => {
          // plot-band should be displayed only when at least one of highlighted queues is visible
          const shouldDisplay = highlightedQueueIds.find((queueId) => dataPerQueueVisibility[queueId]);

          if (shouldDisplay) {
            plotBands.push({
              from: start - halfOfInterval + PLOT_BAND_LEFT_ALIGN_OFFSET,
              to: end - halfOfInterval - PLOT_BAND_RIGHT_ALIGN_OFFSET,
              color: PLOT_BAND_COLOR,
            });
          }

          return plotBands;
        }, [] as XAxisPlotBandsOptions[]),
      tickInterval: interval,
    },
    yAxis: {
      ...staticChartOptions.yAxis,
      min: yAxisTicks[0],
      max: yAxisTicks[yAxisTicks.length - 1],
      tickAmount: 5,
      tickInterval: yAxisTicks[1] - yAxisTicks[0],
    },
  };
};

export const SchedulingChartComponent: React.FC<SchedulingChartProps> = (props) => {
  const { locale, localeObj } = useAuth();

  const { width } = props;

  return (
    <div style={{ width }} data-test-id="scheduling-composer:scheduling-chart">
      <HighchartsReact highcharts={Highcharts} options={generateChartConfig(props, locale, localeObj)} />
    </div>
  );
};

export const SchedulingChart = memo(SchedulingChartComponent);
